import React, { useEffect, useState } from 'react';
import { FieldConfig, FieldType } from '../FormBuilder/types';
import FormFieldRenderer from '../FormBuilder/FormFieldRenderer';
import { t } from '@configuratorware/configurator-frontendgui/Framework/i18n';
import Grid from '@material-ui/core/Grid';
import { Divider } from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import { loadCountries, loadProductParameterForm } from '../../Reducers/App/Actions';
import Subtitle from '../Header/Subtitle';
import { makeStyles } from '@material-ui/core/styles';
import { darkBlue, white } from '../../Constants/Colors';
import {
  getCountries,
  getProductParameterFormData,
  getSelectedProductType,
} from '../../Reducers/App/Selectors';
import { ProductParameterWarningDialog } from './ProductParameterWarningDialog';

const staticProductParameterFields: FieldConfig[] = [
  {
    type: FieldType.DIVIDER,
  },
  {
    type: FieldType.TEXT_FIELD,
    identifier: 'customer',
    placeholderSlug: 'specify',
    label: 'productParameters.customer',
    validation: {
      required: true,
    },
    errorMessage: 'validation.required',
  },
  {
    type: FieldType.TEXT_FIELD,
    identifier: 'contact_person',
    placeholderSlug: 'specify',
    label: 'productParameters.contactPerson',
    validation: {
      required: true,
    },
    errorMessage: 'validation.required',
  },
  {
    type: FieldType.TEXT_FIELD,
    identifier: 'street',
    placeholderSlug: 'specify',
    label: 'productParameters.street',
    validation: {
      required: true,
    },
    errorMessage: 'validation.required',
  },
  {
    type: FieldType.TEXT_FIELD,
    identifier: 'city',
    placeholderSlug: 'specify',
    label: 'productParameters.city',
    validation: {
      required: true,
    },
    errorMessage: 'validation.required',
  },
];
export const useStyles = makeStyles((theme) => ({
  root: {
    background: darkBlue,
    flex: 1,
    color: white,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    padding: '0 2rem',
  },
  scrollableContainer: {
    height: '100%',
    overflowY: 'auto',
    overflowX: 'hidden',
  },
  componentContainer: {
    display: 'grid',
    gridTemplateColumns: 'max(350px, 45%) max(350px, 45%)',
    columnGap: '10%',
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr',
      columnGap: '0',
    },
  },
  component: {},
  divider: {
    gridColumn: '1 / -1',
    borderBottom: '1px solid white',
    margin: '24px 0',
  },
  labelContainer: {
    padding: '16px 0',
    '& label': {
      display: 'block',
      margin: theme.spacing(1, 1, 1, 0),
      fontSize: '1.2em',
    },
  },
  subTitle: {
    marginBottom: '1.5rem',
  },
  termsContainer: {
    padding: '20px 14px',
    '& .MuiTypography-root': {
      fontSize: '0.98em',
    },
  },
  select: {
    background: white,
    '& .MuiSelect-icon': {
      color: darkBlue,
    },
  },
  checkbox: {
    color: white,
    '& .MuiIconButton-label': {
      color: white,
    },
  },
  formControlLabel: {
    '& .MuiTypography-body1': {
      color: `${white} !important`,
    },
  },
}));

export const ProductParameters = () => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const productIdentifier = useSelector(getSelectedProductType)?.identifier;
  const formData = useSelector(getProductParameterFormData);
  const countries = useSelector(getCountries);

  const countryField: FieldConfig = {
    type: FieldType.AUTOCOMPLETE,
    identifier: 'country',
    label: 'productParameters.country',
    placeholderSlug: 'pleaseChoose',
    options: countries.map(({ identifier, title }) => ({ value: identifier, label: title })),
    validation: { required: true },
    errorMessage: 'validation.required',
  };

  const productParameterFields = [...staticProductParameterFields, countryField];

  const [fields, setFields] = useState(productParameterFields);

  useEffect(() => {
    productIdentifier && dispatch(loadProductParameterForm());
    dispatch(loadCountries());
  }, [productIdentifier, dispatch]);

  useEffect(() => {
    formData?.formFields && setFields([...formData.formFields, ...productParameterFields]);
  }, [formData]);

  useEffect(() => {
    const options = countries.map(({ identifier, title }) => ({ value: identifier, label: title }));
    setFields((prev) =>
      prev.map((field) => (field.identifier === 'country' ? { ...field, options } : field))
    );
  }, [countries]);

  return (
    <div className={classes.root}>
      <Subtitle className={classes.subTitle}>{t('productParametersScreen.subtitle')}</Subtitle>
      <div className={classes.scrollableContainer}>
        <div className={classes.componentContainer}>
          {fields.map((field) => {
            if (field.type === FieldType.DIVIDER) {
              return <Divider className={classes.divider} key={`divider-${field.identifier}`} />;
            }
            return (
              <Grid key={field.identifier} container className={classes.component}>
                <Grid item xs={6} className={classes.labelContainer}>
                  <label>
                    {t(field.label)}
                    {field.validation?.required && ' *'}
                  </label>
                </Grid>
                <Grid item xs={6}>
                  <FormFieldRenderer field={field} key={`renderer-${field.identifier}`} />
                </Grid>
              </Grid>
            );
          })}
        </div>
      </div>
      <ProductParameterWarningDialog />
    </div>
  );
};

export default ProductParameters;
